import {
  Platform,
  TouchableOpacity,
  useWindowDimensions,
  View,
  Text as TextBase,
  type TextProps,
  StyleSheet,
} from 'react-native'
import { type ComponentRef, forwardRef, type ComponentProps, type PropsWithChildren } from 'react'
import { CreditCardIcon } from '@/src/assets/icons/CreditCard'
import { CoinIcon } from '@/src/assets/icons/Coin'
import { Link } from 'expo-router'
import { type SegmentAction, triggerSegmentAction } from '@/src/services/segment'
import { PointsBalance } from '@/src/common/modules/CardManagementModule/PointsBalance'
import { AccountIcon } from '@/src/assets/icons/AccountIcon'
import { StatementIcon } from '@/src/assets/icons/StatementIcon'
import { maxWidth, useMainWrapperPadding } from '@/src/common/wrappers/MainWrapper'
import { useMediaQuery } from 'react-responsive'
import { viewingVirtualCardDisabled } from '@/src/app/main/(tabs)/card/card-management/virtual-card/_layout'
import { Tooltip } from '@/src/components/ui/panels/Tooltip'
import { MobileAppInstallCta } from '@/src/app/main/(tabs)/card/cardBenefits'
import type { CardActivityFeedBottomSheetHeightStore } from '@/src/experiences/card-activity/components/feed/_highnote/useCardActivityFeedBottomSheetHeightStore'
import { CardPaymentsE2E } from '@/src/experiences/card-payments/e2e'
import { Experiences } from '@/src/experiences'
import { fontFamilies, mesaColorNames, typography } from '@/src/common/theme'
import { safeLineHeight } from '@/src/common/utils/safeLineHeight'
import * as CardActivity from '@/src/experiences/card-activity'
import { Skeleton } from '@/src/design-system/Skeleton'
import type { BalanceComponentType } from '@/src/experiences/card-payments/screens/MakePayment/Balance'
import { useLegacyCard } from '@/src/common/hooks/useCardInformation'
import { FinancialAccountStatus } from '@api/highnote/graphql'
function Text({ style, ...restOfProps }: TextProps) {
  return (
    <TextBase
      style={[style, { fontFamily: fontFamilies['PlusJakartaSans-Bold'], fontWeight: 700 }]}
      {...restOfProps}
    />
  )
}

function CardDetail({
  name,
  value,
}: {
  name: string
  /** use `undefined` to display a loading skeleton */
  value?: PropsWithChildren['children']
}) {
  return (
    <View style={styles.cardDetailContainer}>
      <Text style={styles.currentBalance}>{name}</Text>
      {typeof value === 'string' ? (
        <Text
          style={[
            typography['label/sm-bold'],
            { color: mesaColorNames['Mesa - Brand/White #FFFFFF'] },
          ]}
        >
          {value}
        </Text>
      ) : (
        <Skeleton style={{ height: 17.5, width: 88 }} />
      )}
    </View>
  )
}

const CardActionWrapper = forwardRef<
  ComponentRef<typeof TouchableOpacity>,
  Pick<ComponentProps<typeof TouchableOpacity>, 'style' | 'disabled' | 'onPress' | 'testID'> & {
    name: string
    segmentAction: SegmentAction
  } & PropsWithChildren
>(
  (
    {
      name,
      children,
      segmentAction,
      // spread is required to allow `asChild`-supported components to properly pass their props to this component's top-level element
      ...props
    },
    ref,
  ) => (
    <TouchableOpacity
      ref={ref}
      // this spread is required. see comment on the `...props` destructuring
      {...props}
      onPress={(...args) => {
        void triggerSegmentAction({
          component: 'Card Management Button',
          action: segmentAction,
        })
        return props.onPress?.(...args)
      }}
    >
      <View
        style={[
          styles.cardActionIconContainer,
          props.disabled && { backgroundColor: mesaColorNames['semantic/fg/disabled #898D8F'] },
        ]}
      >
        {children}
      </View>
      <Text
        style={[
          typography['label/xs-bold'],
          { color: mesaColorNames['Mesa - Brand/White #FFFFFF'] },
          safeLineHeight(typography['label/xs-bold'].lineHeight),
        ]}
        numberOfLines={1}
      >
        {name}
      </Text>
    </TouchableOpacity>
  ),
)

export function useCardManagementModuleMarginVertical() {
  const minWidth1024 = useMediaQuery({ minWidth: 1024 })
  return minWidth1024 || Platform.OS === 'web' ? 40 : 0
}

export function CardManagementModule({
  currentBalance,
  minimumPayment,
  statementDueOn: dueDate,
  statementBalance: remainingStatementBalance,
  availableCredit,
  cardActivityFeedBottomSheetHeightStore,
}: ComponentProps<BalanceComponentType> & {
  cardActivityFeedBottomSheetHeightStore: CardActivityFeedBottomSheetHeightStore
}) {
  const minWidth1200 = useMediaQuery({ minWidth: 1200 })
  const minWidth640 = useMediaQuery({ minWidth: 640 })
  const minWidth393 = useMediaQuery({ minWidth: 393 })
  const { width: windowWidth } = useWindowDimensions()
  const marginHorizontal = minWidth1200 ? 'auto' : minWidth640 ? 24 : minWidth393 ? 16 : 14
  const width = windowWidth - (typeof marginHorizontal === 'number' ? marginHorizontal : 40) * 2
  const isCardActivityFeedBottomSheetFullHeight =
    CardActivity.Components.Highnote.useIsCardActivityFeedBottomSheetFullHeight()
  const mainWrapperPadding = useMainWrapperPadding()
  const marginVertical = useCardManagementModuleMarginVertical()
  const cardInformation = useLegacyCard().data?.getCreditCardInformation.node
  const financialAccountStatus = cardInformation?.financialAccount.accountStatus
  const isFinancialAccountClosed = financialAccountStatus === FinancialAccountStatus.Closed

  return (
    <View
      style={[
        {
          backgroundColor: mesaColorNames['Mesa - Brand/Secondary #223843'],
          maxHeight: '100%',
          overflow: 'scroll',
        },
        isCardActivityFeedBottomSheetFullHeight
          ? { display: 'flex', flexDirection: 'row' }
          : { paddingBottom: Math.min(123, cardActivityFeedBottomSheetHeightStore.minHeight) },
      ]}
    >
      <View
        style={[
          styles.container,
          { marginHorizontal, width },
          isCardActivityFeedBottomSheetFullHeight && {
            flex: 1,
            marginRight: 0,
            marginLeft: 'auto',
            paddingLeft: mainWrapperPadding,
          },
          marginVertical ? { marginTop: marginVertical } : { marginVertical },
        ]}
        onLayout={(e) =>
          cardActivityFeedBottomSheetHeightStore.setHeightAbove(e.nativeEvent.layout.height)
        }
      >
        <Experiences.Notifications.Components.EnablePermissionsBanner text="Never miss any of Mesa's great offers and programs!" />

        <View style={styles.headerContainer}>
          <View style={styles.pointsContainer}>
            <PointsBalance />
          </View>
        </View>
        <View style={styles.infoContainer}>
          <View style={styles.infoCard}>
            <CardDetail name="Current Balance" value={currentBalance?.formatted} />
            <CardDetail name="Minimum Payment" value={minimumPayment?.formatted} />
            <CardDetail name="Payment Due Date" value={dueDate?.formatted} />
            <CardDetail
              name="Remaining Statement Balance"
              value={remainingStatementBalance?.formatted}
            />
            <CardDetail name="Available Credit" value={availableCredit?.formatted} />
          </View>
          <View
            style={[
              styles.actionsContainer,
              isCardActivityFeedBottomSheetFullHeight && { paddingBottom: marginVertical },
            ]}
          >
            <View
              style={[
                styles.cardActionWrapper,
                { display: 'flex', flexDirection: 'column', alignItems: 'stretch' },
              ]}
            >
              <Tooltip
                delay={88}
                content={
                  <Text
                    style={[
                      typography.XS,
                      {
                        backgroundColor: mesaColorNames['semantic/bg/disabled #DADDDE'],
                        padding: 16,
                        borderRadius: 16,
                        color: mesaColorNames['Greyscale/900 #152438'],
                        lineHeight: 20,
                      },
                    ]}
                  >
                    <MobileAppInstallCta
                      style={{
                        zIndex: 1,
                        backgroundColor: 'transparent',
                        padding: 0,
                        color: mesaColorNames['Mesa Primary Scale/500 #00C7B6'],
                      }}
                      children="Install the Mesa App"
                    />
                    {` to view your card details.
Support for viewing card details on web is coming soon`}
                  </Text>
                }
                arrowProps={{
                  style: { backgroundColor: mesaColorNames['semantic/bg/disabled #DADDDE'] },
                }}
              >
                <Link asChild push href="/main/card/card-management/virtual-card">
                  <CardActionWrapper
                    segmentAction='User Pressed the "Virtual Card" button in the Card Management component'
                    name="Card"
                    disabled={viewingVirtualCardDisabled}
                    style={{ width: '100%', gap: 8, alignItems: 'center' }}
                  >
                    <CreditCardIcon />
                  </CardActionWrapper>
                </Link>
              </Tooltip>
            </View>

            {isFinancialAccountClosed ? (
              <CardActionWrapper
                segmentAction='User Pressed the "Pay" button in the Card Management component'
                name="Pay"
                testID={CardPaymentsE2E.Ids.Press.Pay}
                style={styles.cardActionWrapper}
                disabled={isFinancialAccountClosed}
              >
                <CoinIcon />
              </CardActionWrapper>
            ) : (
              <Link asChild push href="/main/card/card-management/payment">
                <CardActionWrapper
                  segmentAction='User Pressed the "Pay" button in the Card Management component'
                  name="Pay"
                  testID={CardPaymentsE2E.Ids.Press.Pay}
                  style={styles.cardActionWrapper}
                >
                  <CoinIcon />
                </CardActionWrapper>
              </Link>
            )}
            <Link asChild push href="/main/card/card-management/accounts">
              <CardActionWrapper
                name="Accounts"
                segmentAction='User Pressed the "Accounts" button in the Card Management component'
                style={styles.cardActionWrapper}
              >
                <AccountIcon />
              </CardActionWrapper>
            </Link>
            <Link asChild push href="/main/card/card-management/statements">
              <CardActionWrapper
                name="Statements"
                segmentAction='User Pressed the "Statements" button in the Card Management component'
                style={styles.cardActionWrapper}
              >
                <StatementIcon />
              </CardActionWrapper>
            </Link>
          </View>
        </View>
      </View>
      {isCardActivityFeedBottomSheetFullHeight && (
        <View style={{ maxWidth: maxWidth / 2, flex: 1, marginRight: 'auto' }} />
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    padding: 15,
    paddingTop: 0,
    display: 'flex',
    gap: 12,
    maxWidth: maxWidth / 2,
  },
  cardDetailContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  whiteText: {
    color: mesaColorNames['Mesa - Brand/White #FFFFFF'],
  },
  infoContainer: {
    display: 'flex',
    gap: 24,
  },
  infoCard: {
    display: 'flex',
    gap: 8,
  },
  currentBalance: {
    color: mesaColorNames['Mesa - Brand/White #FFFFFF'],
    fontSize: 14,
  },
  actionsContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-around',
    gap: 12,
  },
  cardActionWrapper: {
    alignContent: 'center',
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    flexShrink: 1,
    flexBasis: 72,
    maxWidth: 72,
    gap: 8,
  },
  cardActionIconContainer: {
    padding: 12,
    width: '100%',
    aspectRatio: 1,
    height: 'auto',
    backgroundColor: mesaColorNames['Mesa Primary Scale/500 #00C7B6'],
    borderRadius: 50,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: mesaColorNames['Mesa - Brand/Blackish #222222'],
    shadowOffset: {
      width: 0,
      height: 0,
    },
    shadowOpacity: 0.37,
    shadowRadius: 6,
    elevation: 0,
  },
  headerContainer: {
    display: 'flex',
    gap: 12,
  },
  pointsContainer: {
    height: 32,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-end',
    width: '100%',
  },
})
